import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, ApiFailure, type RevenueWebsite } from '../../api';
import { Icon } from '../Icon';

/**
 * Pieces every web-analytics page shares: the site picker, the date range,
 * number formatting, period-over-period deltas, and the empty state for a
 * workspace with no site yet. The site and range persist across pages (and
 * reloads), so switching from Traffic to Realtime to Marketing keeps context.
 */

// ---------------------------------------------------------------------------
// Persistence (per-viewer convenience only; every read is guarded)
// ---------------------------------------------------------------------------

export function readPref(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the preference simply is not remembered */
  }
}

// ---------------------------------------------------------------------------
// Websites
// ---------------------------------------------------------------------------

export function useWebsites() {
  const [websites, setWebsites] = useState<RevenueWebsite[] | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [selected, setSelectedState] = useState<string>(() => readPref('dv-site') ?? '');

  const reload = useCallback(() => {
    api
      .listRevenueWebsites()
      .then((data) => {
        setWebsites(data.websites);
        setSelectedState((current) => {
          if (data.websites.some((w) => w.id === current)) return current;
          return data.websites[0]?.id ?? '';
        });
      })
      .catch((error: unknown) => {
        setWebsites([]);
        setFailure(error instanceof ApiFailure ? error : new ApiFailure('Could not load websites.', 'Try again.', 0));
      });
  }, []);

  useEffect(reload, [reload]);

  const setSelected = useCallback((id: string) => {
    setSelectedState(id);
    writePref('dv-site', id);
  }, []);

  return { websites, selected, setSelected, failure, reload };
}

export function SitePicker({
  websites,
  selected,
  onChange,
}: {
  websites: RevenueWebsite[];
  selected: string;
  onChange: (id: string) => void;
}) {
  if (websites.length === 1) {
    return (
      <span className="site-pill" title={websites[0]!.domain}>
        <span className="site-favicon" aria-hidden>
          {websites[0]!.domain.slice(0, 1).toUpperCase()}
        </span>
        {websites[0]!.domain}
      </span>
    );
  }
  return (
    <label className="site-select">
      <span className="sr-only">Website</span>
      <select value={selected} onChange={(e) => onChange(e.target.value)}>
        {websites.map((w) => (
          <option key={w.id} value={w.id}>
            {w.domain}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Date ranges
// ---------------------------------------------------------------------------

export type RangePreset = 'today' | 'yesterday' | '24h' | '7d' | '30d' | 'mtd' | 'lastMonth' | '90d' | '12mo' | 'custom';

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  mtd: 'Month to date',
  lastMonth: 'Last month',
  '90d': 'Last 90 days',
  '12mo': 'Last 12 months',
  custom: 'Custom range',
};

export interface DateRange {
  preset: RangePreset;
  /** ISO instants (start inclusive, end inclusive). */
  start: string;
  end: string;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Resolve a preset against the viewer's local calendar. */
export function resolveRange(preset: RangePreset, custom?: { from: string; to: string }): DateRange {
  const now = new Date();
  const days = (n: number) => new Date(now.getTime() - n * 86_400_000);
  let start: Date;
  let end: Date = now;
  switch (preset) {
    case 'today':
      start = startOfDay(now);
      break;
    case 'yesterday':
      start = startOfDay(days(1));
      end = endOfDay(days(1));
      break;
    case '24h':
      start = days(1);
      break;
    case '7d':
      start = startOfDay(days(6));
      break;
    case '30d':
      start = startOfDay(days(29));
      break;
    case 'mtd':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'lastMonth':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
      break;
    case '90d':
      start = startOfDay(days(89));
      break;
    case '12mo':
      start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      break;
    case 'custom': {
      const from = custom?.from ? new Date(`${custom.from}T00:00:00`) : startOfDay(days(29));
      const to = custom?.to ? new Date(`${custom.to}T23:59:59.999`) : now;
      start = from;
      end = to;
      break;
    }
  }
  return { preset, start: start.toISOString(), end: end.toISOString() };
}

export function useDateRange(storageKey = 'dv-range', fallback: RangePreset = '30d') {
  const [preset, setPresetState] = useState<RangePreset>(() => {
    const stored = readPref(storageKey) as RangePreset | null;
    return stored !== null && stored in RANGE_LABELS && stored !== 'custom' ? stored : fallback;
  });
  const [custom, setCustom] = useState<{ from: string; to: string }>(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 29 * 86_400_000);
    return { from: localDate(from), to: localDate(to) };
  });
  // Recompute on demand so "Today" stays today after midnight.
  const [tick, setTick] = useState(0);
  const range = useRangeMemo(preset, custom, tick);
  const setPreset = (next: RangePreset) => {
    setPresetState(next);
    if (next !== 'custom') writePref(storageKey, next);
  };
  return { range, preset, setPreset, custom, setCustom, refresh: () => setTick((t) => t + 1) };
}

function useRangeMemo(preset: RangePreset, custom: { from: string; to: string }, tick: number): DateRange {
  const [range, setRange] = useState(() => resolveRange(preset, custom));
  useEffect(() => {
    setRange(resolveRange(preset, custom));
  }, [preset, custom.from, custom.to, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  return range;
}

export function localDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function RangePicker({
  preset,
  onPreset,
  custom,
  onCustom,
  presets = ['today', 'yesterday', '7d', '30d', 'mtd', 'lastMonth', '90d', '12mo', 'custom'],
}: {
  preset: RangePreset;
  onPreset: (p: RangePreset) => void;
  custom: { from: string; to: string };
  onCustom: (c: { from: string; to: string }) => void;
  presets?: RangePreset[];
}) {
  return (
    <div className="range-picker">
      <label className="range-select">
        <Icon name="calendar" size={15} />
        <span className="sr-only">Date range</span>
        <select value={preset} onChange={(e) => onPreset(e.target.value as RangePreset)}>
          {presets.map((p) => (
            <option key={p} value={p}>
              {RANGE_LABELS[p]}
            </option>
          ))}
        </select>
      </label>
      {preset === 'custom' && (
        <span className="range-custom">
          <input
            type="date"
            aria-label="From"
            value={custom.from}
            max={custom.to}
            onChange={(e) => onCustom({ ...custom, from: e.target.value })}
          />
          <span aria-hidden>–</span>
          <input
            type="date"
            aria-label="To"
            value={custom.to}
            min={custom.from}
            onChange={(e) => onCustom({ ...custom, to: e.target.value })}
          />
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function fmtNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${(value / 1000).toFixed(abs >= 100_000 ? 0 : 1)}k`;
  return Math.round(value).toLocaleString('en-US');
}

export function fmtExact(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export function fmtMoney(value: number | null | undefined, opts: { compact?: boolean; currency?: string } = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  // Negative zero (a cost of nothing, negated) must not print as "-$0".
  if (Math.abs(value) < 0.005) value = 0;
  const currency = opts.currency ?? 'USD';
  if (opts.compact === true && Math.abs(value) >= 10_000) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: Math.abs(value) < 100 ? 2 : 0,
    minimumFractionDigits: 0,
  }).format(value);
}

export function fmtPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

export function fmtRatio(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(2)}×`;
}

export function fmtDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export function timeAgo(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * A country-code badge. Not a flag emoji: Windows has no flag glyphs and
 * renders them as two bare letters, so the badge looks the same everywhere.
 */
export function CountryBadge({ code }: { code: string | null | undefined }) {
  if (code === null || code === undefined || !/^[A-Za-z]{2}$/.test(code)) {
    return <span className="cc unknown" aria-hidden>··</span>;
  }
  return <span className="cc" aria-hidden>{code.toUpperCase()}</span>;
}

let regionNames: Intl.DisplayNames | null = null;
export function countryName(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return code;
  try {
    regionNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

// ---------------------------------------------------------------------------
// Deltas
// ---------------------------------------------------------------------------

/**
 * Change against the previous period. `better` says which direction is good,
 * so a falling bounce rate reads as an improvement. Null when there is no
 * baseline to compare with: a change from zero is not a percentage.
 */
export function Delta({
  current,
  previous,
  better = 'up',
  mode = 'relative',
}: {
  current: number | null | undefined;
  previous: number | null | undefined;
  better?: 'up' | 'down';
  /** relative: percent change; points: difference of two percentages. */
  mode?: 'relative' | 'points';
}) {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return <span className="kpi-delta none">—</span>;
  }
  let change: number;
  let label: string;
  if (mode === 'points') {
    change = current - previous;
    label = `${change > 0 ? '+' : ''}${change.toFixed(1)} pts`;
  } else {
    if (previous === 0) return <span className="kpi-delta none" title="No baseline in the previous period">new</span>;
    change = ((current - previous) / Math.abs(previous)) * 100;
    label = `${change > 0 ? '+' : ''}${Math.abs(change) >= 100 ? change.toFixed(0) : change.toFixed(1)}%`;
  }
  if (Math.abs(change) < 0.05) return <span className="kpi-delta flat">0%</span>;
  const good = better === 'up' ? change > 0 : change < 0;
  return (
    <span className={`kpi-delta ${good ? 'good' : 'bad'}`}>
      <Icon name={change > 0 ? 'up' : 'down'} size={12} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

export function PageHeader({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children?: ReactNode }) {
  return (
    <header className="an-header">
      <div className="an-title">
        <h2>{title}</h2>
        {subtitle !== undefined && <p>{subtitle}</p>}
      </div>
      {children !== undefined && <div className="an-toolbar">{children}</div>}
    </header>
  );
}

export function NoWebsite({ onSetup, onUpload, what }: { onSetup: () => void; onUpload?: () => void; what: string }) {
  return (
    <div className="page">
      <section className="onboard">
        <div className="onboard-copy">
          <span className="eyebrow">Get started in 2 minutes</span>
          <h2>Connect your website to see {what}</h2>
          <p>
            Add one script tag. It is under 3&nbsp;KB, sets no cookies by default and needs no consent banner in most
            jurisdictions. Visitors, sources, pages, countries, devices, goals and revenue appear within seconds.
          </p>
          <ol className="onboard-steps">
            <li>
              <strong>Add your site</strong>
              <span>Enter the domain; you get a snippet.</span>
            </li>
            <li>
              <strong>Paste the snippet</strong>
              <span>Into the &lt;head&gt; of every page, or via Google Tag Manager.</span>
            </li>
            <li>
              <strong>Connect payments</strong>
              <span>Stripe, LemonSqueezy, Polar or Shopify, to see revenue per source.</span>
            </li>
          </ol>
          <div className="row">
            <button className="primary" onClick={onSetup}>
              <Icon name="plus" size={15} /> Add a website
            </button>
            {onUpload !== undefined && (
              <button className="ghost" onClick={onUpload}>
                Or analyse a spreadsheet instead
              </button>
            )}
          </div>
        </div>
        <div className="onboard-art" aria-hidden>
          <div className="onboard-card">
            <div className="onboard-kpis">
              <div><span>Visitors</span><strong>—</strong></div>
              <div><span>Revenue</span><strong>—</strong></div>
              <div><span>Conv.</span><strong>—</strong></div>
            </div>
            <svg viewBox="0 0 240 80" className="onboard-spark">
              <path d="M0 64 C30 58 40 40 70 44 S110 20 140 28 S190 10 240 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <p>Your numbers appear here once the first visit arrives.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export function LoadingCard({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="card skeleton-card" role="status">
      <span className="spinner" /> {label}
    </div>
  );
}
